import { useSelector } from "react-redux";
import { useState, useEffect, useRef } from "react";
import Navigation from "../../common/components/Navigation/Navigation";
import { Outlet, useLocation } from "react-router-dom";
import ProjectPreview from "../../common/components/ProjectPreview/ProjectPreview";
import {
  getCurrentElements,
  getSelectedProjectGroup,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { useTranslation } from "react-i18next";
import AddTeamModal from "../../features/team/components/AddTeamModal";
import ParticipantCsv from "../../features/team/components/ParticipantFromCsvModal";
import { useAppDispatch } from "src/common/state/hooks";
import { fetchFilteredMedias, resetMediaSearch } from "src/features/gallery/state/gallerySlice";
import { PROJECT_GROUP_MEDIAS_COMPONENT_ID } from "./ProjectGroupMedias";

// Delay before unmounting Unity WebGL after leaving Preview tab (prevents cleanup race conditions)
// See Story 10.7 for details on GLctx errors and keyboard hijacking during rapid tab switching
const UNITY_UNMOUNT_DELAY_MS = 30000;

const ProjectGroupEdit = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const path = location.pathname.split("/");
  const lastUrlElement = path[path.length - 1];

  const isPreviewRoute = lastUrlElement === "preview";

  // Lazy mount + delayed unmount pattern for Unity WebGL
  // Story 13.3: Only mount when Preview tab is first visited (avoids downloading 5-10MB WebGL assets on other tabs)
  // Story 10.7: Keep mounted for UNITY_UNMOUNT_DELAY_MS after leaving to prevent cleanup race conditions
  const [hasVisitedPreview, setHasVisitedPreview] = useState(false);
  const [shouldRenderPreview, setShouldRenderPreview] = useState(false);
  const unmountTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isPreviewRoute) {
      // First visit to Preview tab - mount the component
      setHasVisitedPreview(true);
      setShouldRenderPreview(true);
      // Clear any pending unmount timeout
      if (unmountTimeoutRef.current) {
        clearTimeout(unmountTimeoutRef.current);
        unmountTimeoutRef.current = null;
      }
    } else if (hasVisitedPreview && shouldRenderPreview) {
      // Leaving Preview tab after having visited - schedule delayed unmount
      unmountTimeoutRef.current = setTimeout(() => {
        setShouldRenderPreview(false);
        setHasVisitedPreview(false);
        unmountTimeoutRef.current = null;
      }, UNITY_UNMOUNT_DELAY_MS);
    }

    return () => {
      // Cleanup timeout on unmount
      if (unmountTimeoutRef.current) {
        clearTimeout(unmountTimeoutRef.current);
      }
    };
  }, [isPreviewRoute, hasVisitedPreview, shouldRenderPreview]);

  const selectedProjectGroup = useSelector(getSelectedProjectGroup);
  const selectedCurrentElements = useSelector(getCurrentElements);

  const publishTabShouldBeVisible = selectedCurrentElements && selectedCurrentElements.length > 0;

  const tabs = [
    { title: t("pages.projectGroup.tabs.list"), urlEndPoint: "list" },
    { title: t("general.preview"), urlEndPoint: "preview" },
    {
      title: t("general.medias"),
      urlEndPoint: "medias",
      onClick: () => {
        dispatch(resetMediaSearch({ resetEverything: true }));
        dispatch(
          fetchFilteredMedias({
            start: 0,
            limit: 20,
            projectGroupId: selectedProjectGroup.id,
            componentId: PROJECT_GROUP_MEDIAS_COMPONENT_ID,
          }),
        );
      },
    },
    { title: t("pages.projectGroup.tabs.team"), urlEndPoint: "team" },
    {
      title: t("pages.projectGroup.tabs.publish"),
      urlEndPoint: "publish",
      hidden: !publishTabShouldBeVisible,
    },
    { title: t("general.information"), urlEndPoint: "information" },
  ];

  return (
    <>
      <Navigation
        title={selectedProjectGroup.name}
        tabs={tabs}
        isProjectGroupView
        showGoBackButton
        backDepth={lastUrlElement === "new" ? 1 : 2}
      />
      {/* Lazy mount + delayed unmount: Unity WebGL preview only mounts when Preview tab is visited */}
      {/* Story 13.3: Prevents background loading on other tabs (saves 5-10MB download) */}
      {/* Story 10.7: Delayed unmount prevents cleanup race conditions during rapid tab switching */}
      {shouldRenderPreview && (
        <div
          className={isPreviewRoute ? "h-full" : "hidden"}
          aria-hidden={!isPreviewRoute}
        >
          <ProjectPreview />
        </div>
      )}
      {/* Outlet renders when not on preview route */}
      <div className={isPreviewRoute ? "hidden" : ""}>
        <Outlet />
      </div>
      <AddTeamModal />
      <ParticipantCsv />
    </>
  );
};

export default ProjectGroupEdit;
